/**
 * QrPosterPage · v2.7.65+E264 (E266 · size options)
 * ==================================================
 * Printable "Scan to book" flyer for pickup spots and local bars. Big QR
 * deep-links to the booking page on the LIVE site. Pick a size before
 * printing: Full Page, Half-Page ×2 (cut into two), or Table Tent (fold in
 * half so both sides face out). Print styles live in App.css (@media print).
 */
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";

const PHONE = "(580) 219-4687";
const SIZES = [
  { id: "full", label: "Full Page" },
  { id: "half", label: "Half-Page ×2" },
  { id: "tent", label: "Table Tent" },
];

function PosterFlyer({ compact, logo, bookingUrl }) {
  return (
    <div className={`flex flex-col items-center text-center w-full h-full ${compact ? "px-5 py-4" : "px-10 py-10"}`}>
      <div className="flex flex-col items-center">
        {logo
          ? <img src={logo} alt="Buller Taxi Services" className={`object-contain ${compact ? "h-11 mb-1" : "h-20 mb-2"}`}/>
          : <div className={`font-black tracking-tight text-zinc-900 ${compact ? "text-xl" : "text-3xl"}`}>BULLER <span className="text-amber-500">TAXI</span></div>}
        <div className={`uppercase tracking-[0.35em] text-zinc-500 font-bold ${compact ? "text-[8px]" : "text-[11px]"}`}>Weatherford, OK · Local Rides</div>
      </div>

      <div className={compact ? "mt-2" : "mt-6"}>
        <div className={`font-black leading-none text-zinc-900 ${compact ? "text-2xl" : "text-5xl"}`}>NEED A RIDE?</div>
        <div className={`inline-block bg-amber-400 text-zinc-950 font-black rounded ${compact ? "text-sm px-2.5 py-1 mt-1.5" : "text-2xl px-4 py-1.5 mt-3"}`}>SCAN TO BOOK IN SECONDS</div>
      </div>

      <div className={`bg-white rounded-2xl ring-4 ring-zinc-900 ${compact ? "mt-2.5 p-2" : "mt-7 p-4"}`}>
        <QRCodeSVG value={bookingUrl} size={compact ? 150 : 280} level="M" fgColor="#0b0b0c" bgColor="#ffffff"/>
      </div>
      <div className={`font-bold text-zinc-500 ${compact ? "text-[10px] mt-1.5" : "text-sm mt-3"}`}>Point your phone camera at the code</div>

      <div className={`w-full ${compact ? "mt-2.5" : "mt-auto pt-6"}`}>
        <div className={`uppercase tracking-widest text-zinc-500 font-bold ${compact ? "text-[9px]" : "text-sm"}`}>Or call us — dispatch answers 24/7</div>
        <a href={`tel:${PHONE.replace(/\D/g, "")}`} className={`block font-black text-red-600 tracking-tight ${compact ? "text-2xl" : "text-5xl mt-1"}`}>{PHONE}</a>
        <div className={`font-black text-zinc-900 ${compact ? "text-xs mt-1" : "text-lg mt-4"}`}>$10 Local Cab Fare · Safe · On Standby</div>
        <div className={`font-bold text-zinc-500 ${compact ? "text-[9px] mt-0.5" : "text-sm mt-1"}`}>bullertaxi.com</div>
      </div>
    </div>
  );
}

export default function QrPosterPage() {
  const { cfg } = useSiteConfig();
  const logo = cfg?.global?.logo_url || "";
  const [size, setSize] = useState("full");
  const loc = typeof window !== "undefined" ? window.location : null;
  const bookingUrl = loc && /bullertaxi\.com$/i.test(loc.hostname)
    ? `${loc.origin}/ride/login`
    : "https://bullertaxi.com/ride/login";

  const flyer = (compact) => <PosterFlyer compact={compact} logo={logo} bookingUrl={bookingUrl}/>;

  return (
    <div className="min-h-screen bg-zinc-200 print:bg-white flex flex-col items-center py-8 px-4">
      <div className="no-print w-full max-w-[720px] flex flex-wrap items-center justify-between gap-3 mb-5" data-testid="qr-poster-toolbar">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-bold text-zinc-700 hover:text-zinc-900"><ArrowLeft className="h-4 w-4"/> Back to site</Link>
        <div className="inline-flex rounded-lg overflow-hidden border border-zinc-400" data-testid="qr-poster-sizes">
          {SIZES.map((s) => (
            <button
              key={s.id}
              onClick={() => setSize(s.id)}
              data-testid={`poster-size-${s.id}`}
              className={`px-3.5 py-2 text-xs font-extrabold uppercase tracking-wide transition-colors ${size === s.id ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => window.print()}
          data-testid="qr-poster-print"
          className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-wider px-5 py-2.5 rounded-md"
        >
          <Printer className="h-4 w-4"/> Print / Save PDF
        </button>
      </div>

      <div className="qr-poster bg-white w-full max-w-[720px] aspect-[8.5/11] shadow-2xl border border-zinc-200 flex flex-col" data-testid="qr-poster">
        {size === "full" && (
          <div className="flex-1 min-h-0" data-testid="qr-poster-full">{flyer(false)}</div>
        )}
        {size === "half" && (
          <div className="flex-1 min-h-0 flex flex-col" data-testid="qr-poster-half">
            <div className="flex-1 min-h-0 border-b-2 border-dashed border-zinc-300">{flyer(true)}</div>
            <div className="flex-1 min-h-0">{flyer(true)}</div>
          </div>
        )}
        {size === "tent" && (
          <div className="flex-1 min-h-0 flex flex-col" data-testid="qr-poster-tent">
            <div className="flex-1 min-h-0 rotate-180">{flyer(true)}</div>
            <div className="text-center text-[9px] text-zinc-400 uppercase tracking-[0.3em] font-bold border-t-2 border-dashed border-zinc-400 py-0.5">— fold here —</div>
            <div className="flex-1 min-h-0">{flyer(true)}</div>
          </div>
        )}
      </div>

      <p className="no-print text-xs text-zinc-500 mt-4 max-w-[720px] text-center">
        {size === "full" && "Full-page flyer for windows and boards."}
        {size === "half" && "Two identical half-page flyers — print and cut along the dashed line."}
        {size === "tent" && "Table tent — print, fold along the dashed line, and stand it on the counter (both sides face out)."}
        {" "}The QR opens <span className="font-semibold">{bookingUrl}</span>.
      </p>
    </div>
  );
}
